import {
    collection,
    getVariantByParentType,
    globalCounterPath,
    parentTypeFor,
    readGlobalCount
} from "../_variants/index.js";
import {
    isPrototypeRevision,
    makeAttached,
    makeDropdown,
    pad
} from "../../../utils.js";

export const addServiceBulletin = ({ form, pageItem, store, attachedItems, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");
    const sbCollectionTail = parentTypeFor(variant, "ServiceBulletins");
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = parentTypeFor(variant, "Documents");
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = parentTypeFor(variant, "Drawings");
    const effectivityCollection = "Database/Effectivities";
    const effectivityCollectionTail = "Effectivities";
    const counterPath = globalCounterPath(variant, "counts/database/service_bulletin/count");

    form.setConfig({
        "title": "Add Service Bulletin",
        "type": "push",
        "location": sbCollection,
        "fields": [{
            "label": "Number",
            "type": "value",
            value() {
                return `${variant.docPrefix}SB-${pad(readGlobalCount(store, variant, ["database", "service_bulletin"]))}`
            }
        }, {
            "label": "Comment",
            "name": "Comment",
            "type": "text",
            "divClass": "col-span-2"
        }, {
            "label": "Select Documents",
            "name": docCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems?.[docCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Drawings",
            "name": drawingCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[drawingCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Effectivities",
            "name": effectivityCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[effectivityCollectionTail].items, "Number", "ID")
        }],
        async preSubmitHook({ data, db }) {
            const number = await db.utils.allocateCount(counterPath);
            return {
                "Date": {
                    "Created": (new Date()).toISOString(),
                    "Cancelled": "N/A",
                    "Signedoff": "N/A"
                },
                "Cancelled": false,
                "Signedoff": false,
                "Aircraft": pageItem.Aircraft,
                "Author": store.getters.userName,
                "Parent": pageItem.Number,
                "ParentID": pageItem.ID,
                "Parents": {
                    [pageItem.ID]: true
                },
                "ParentType": meta.ParentType,
                "Number": `${variant.docPrefix}SB-${pad(number)}`,
                [docCollectionTail]: makeAttached(data[docCollectionTail]),
                [drawingCollectionTail]: makeAttached(data[drawingCollectionTail]),
                [effectivityCollectionTail]: makeAttached(data[effectivityCollectionTail]),
                "Revision": 1,
                "RevisionID": "--R1",
                "Revisions": {
                    "--R1": {
                        "Revision": 1,
                        "Date": (new Date()).toISOString(),
                        "Reason": "Service Bulletin Created",
                        "ID": "--R1"
                    }
                }
            }
        },
        async postSubmitHook({ db, newData }) {
            await db.set(`Database/${meta.ParentType}/${pageItem.ID}/${sbCollectionTail}/${newData.ID}`, true);

            for (let doc of Object.keys(newData[docCollectionTail] || {})) {
                await db.set(`${docCollection}/${doc}/${sbCollectionTail}/${newData.ID}`, true);
            }

            for (let drawing of Object.keys(newData[drawingCollectionTail] || {})) {
                await db.set(`${drawingCollection}/${drawing}/${sbCollectionTail}/${newData.ID}`, true);
            }

            for (let effectivity of Object.keys(newData[effectivityCollectionTail] || {})) {
                await db.set(`${effectivityCollection}/${effectivity}/${sbCollectionTail}/${newData.ID}`, true);
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const editServiceBulletin = async ({ form, item, attachedItems, store, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");
    const sbCollectionTail = parentTypeFor(variant, "ServiceBulletins");
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = parentTypeFor(variant, "Documents");
    const drawingCollection = collection(variant, "Drawings");
    const drawingCollectionTail = parentTypeFor(variant, "Drawings");
    const effectivityCollection = "Database/Effectivities";
    const effectivityCollectionTail = "Effectivities";

    form.setValues({
        "Comment": item.Comment,
        [docCollectionTail]: Object.keys(item[docCollectionTail] || {}),
        [drawingCollectionTail]: Object.keys(item[drawingCollectionTail] || {}),
        [effectivityCollectionTail]: Object.keys(item[effectivityCollectionTail] || {})
    });
    form.setConfig({
        "title": "Update Service Bulletin",
        "type": "update",
        "location": `${sbCollection}/${item.ID}`,
        "set": true,
        "fields": [{
            "label": "Comment",
            "name": "Comment",
            "type": "text",
            "divClass": "col-span-3"
        }, {
            "label": "Select Documents",
            "name": docCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[docCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Drawings",
            "name": drawingCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[drawingCollectionTail].items, "Number", "ID")
        }, {
            "label": "Select Effectivities",
            "name": effectivityCollectionTail,
            "type": "dropdown",
            "multiple": true,
            "required": false,
            "dropdownData": makeDropdown(store, attachedItems[effectivityCollectionTail].items, "Number", "ID")
        }],
        preSubmitHook({ data }) {
            return {
                [docCollectionTail]: makeAttached(data[docCollectionTail]),
                [drawingCollectionTail]: makeAttached(data[drawingCollectionTail]),
                [effectivityCollectionTail]: makeAttached(data[effectivityCollectionTail])
            }
        },
        async postSubmitHook({ db, newData }) {
            for (let doc of Object.keys(newData[docCollectionTail] || {})) {
                await db.set(`${docCollection}/${doc}/${sbCollectionTail}/${newData.ID}`, true);
            }

            for (let drawing of Object.keys(newData[drawingCollectionTail] || {})) {
                await db.set(`${drawingCollection}/${drawing}/${sbCollectionTail}/${newData.ID}`, true);
            }

            for (let effectivity of Object.keys(newData[effectivityCollectionTail] || {})) {
                await db.set(`${effectivityCollection}/${effectivity}/${sbCollectionTail}/${newData.ID}`, true);
            }
        }
    });
    await form.getDropdownData();

    form.toggle();
};

export const cancelServiceBulletin = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");

    form.setConfig({
        "title": "Cancel ServiceBulletin",
        "description": `Are you sure you want to cancel ServiceBulletin ${item.Number}?`,
        "type": "update",
        "location": `${sbCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Cancelled": true,
                "Date/Cancelled": new Date().toISOString()
            }
        }
    });
    form.toggle();
};

export const signoffServiceBulletin = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");

    form.setConfig({
        "title": "Signoff Service Bulletin",
        "description": `Are you sure you want to signoff Service Bulletin ${item.Number}?`,
        "type": "update",
        "location": `${sbCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Signedoff": true,
                "Date": {
                    "Signedoff": new Date().toISOString()
                }
            }
        }
    });
    form.toggle();
};

export const linkServiceBulletin = ({ form, pageItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");
    const sbCollectionTail = parentTypeFor(variant, "ServiceBulletins");

    form.setConfig({
        "title": "Link ServiceBulletin",
        "type": "update",
        "location": `Database/${meta.ParentType}/${pageItem.ID}`,
        "fields": [{
            "label": "ServiceBulletin",
            "name": "ServiceBulletin",
            "type": "dropdown",
            "labelKey": "Number",
            "idKey": "ID",
            "dataLocation": sbCollection
        }],
        preSubmitHook({ liveInputData }) {
            return {
                "ServiceBulletin": null,
                [`${sbCollectionTail}/${liveInputData.ServiceBulletin}`]: true
            }
        },
        async postSubmitHook({ db, newData, liveInputData }) {

            await db.set(`${sbCollection}/${liveInputData.ServiceBulletin}/${meta.ParentType}/${pageItem.ID}`, true);
        }
    });
    form.getDropdownData();

    form.toggle();
};

export const unlinkServiceBulletin = ({ form, pageItem, rowItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");
    const sbCollectionTail = parentTypeFor(variant, "ServiceBulletins");

    form.setConfig({
        "title": "Unlink ServiceBulletin",
        "type": "delete",
        "desctiption": `Are you sure you want to unlink ServiceBulletin ${rowItem.Number}?`,
        "location": `Database/${meta.ParentType}/${pageItem.ID}/${sbCollectionTail}/${rowItem.ID}`,
        "fields": [],

        postSubmitHook({ db, liveInputData }) {
            db.set(`${sbCollection}/${rowItem.ID}/${meta.ParentType}/${pageItem.ID}`, null);
        }
    });
    form.getDropdownData();

    form.toggle();
}

export const reviseServiceBulletin = ({ form, pageItem, item, store, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const sbCollection = collection(variant, "ServiceBulletins");

    // Calculate the next revision
    let nextRevision = isPrototypeRevision(item.Revision) ? `P${parseInt(item.Revision.split("P")[1]) + 1}` : parseInt(item.Revision) + 1;
    let nextRevisionID = `--R${nextRevision}`;
    form.setItem(item);
    form.setConfig({
        "title": "Revise Service Bulletin",
        "description": `Revise Service Bulletin <strong class="text-primary">${item.Number}</strong> from Revision <strong class="text-primary">${item.Revision}</strong> to Revision <strong class="text-primary">${nextRevision}</strong>.`,
        "location": `${sbCollection}/${item.ID}`,
        "type": "update",
        "fields": [{ 
            "label": "Reason",
            "name": `Revisions/${nextRevisionID}/Reason`,
            "type": "text",
            "divClass": "md:cols-span-3"
        }],
        preSubmitHook({ data }) {
            return {
                "Revision": nextRevision,
                "RevisionID": nextRevisionID,
                "Signedoff": false,
                "Revisions": {
                    [nextRevisionID]: {
                        "Date": (new Date()).toISOString(),
                        "Revision": nextRevision,
                        "RevisedBy": store.state.user.Basic.name,
                        "ID": nextRevisionID
                    }
                }
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};
